import './styles/default.scss';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import {
  BRAND,
  IN_GAME_MERCH_ABC_KEY,
  InGameMerchOrchestrator,
} from '@fanduel/rewards-hub-client/in-game-merch';
import '@fanduel/formation-tokens/build/css/casino.css';
import { ModalPresenter } from '@fanduel/cpe-um-bundle/core-modal-presenter/library';
import { useStore } from '@/store/useStore';
import { removeFromQueueSpecific } from '@/store/actions';
import { POPUP_TYPES } from '@/features/Popup/constants';
import { getBaseURL } from '@/utilities/getters/gameContextGetters';
import type { InGameMerchQueueData } from './types';

const IN_GAME_MERCH_CONFIG = {
  rewardsApiUrl: `${getBaseURL()}/api/rewards`,
  pphApi: 'https://usw2-promos-api.fanduel.casino.cert.fndl.dev',
};

// The orchestrator resolves what (if anything) to render asynchronously (e.g.
// fetching rewards components) and may end up rendering nothing at all, e.g.
// on a failed request. Give it a moment, then drop the popup rather than leave
// an empty overlay sitting on top of the game.
const DISMISS_IF_EMPTY_AFTER_MS = 5000;

export type InGameMerchOverlayProps = {
  data: InGameMerchQueueData;
};

export const InGameMerchOverlay = ({ data }: InGameMerchOverlayProps) => {
  const gameId = useStore((s) => s.gameContext.gameId);
  const brand = useStore((s) => s.runtime.brand) ?? BRAND.FANDUEL;
  const pausable = data.pausable ?? false;

  const contentRef = useRef<HTMLDivElement>(null);
  const [hasVisibleContent, setHasVisibleContent] = useState(false);

  useEffect(() => {
    const node = contentRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(([entry]) => {
      setHasVisibleContent(entry.contentRect.height > 0);
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const handleDismiss = () => {
    removeFromQueueSpecific({ type: POPUP_TYPES.IN_GAME_MERCH });
    ModalPresenter.dismissAllModals();
  };

  const handleDismissRef = useRef(handleDismiss);
  useEffect(() => {
    handleDismissRef.current = handleDismiss;
  });

  const hasVisibleContentRef = useRef(hasVisibleContent);
  useEffect(() => {
    hasVisibleContentRef.current = hasVisibleContent;
  }, [hasVisibleContent]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasVisibleContentRef.current) {
        handleDismissRef.current();
      }
    }, DISMISS_IF_EMPTY_AFTER_MS);

    return () => clearTimeout(timer);
  }, []);

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      handleDismiss();
    }
  };

  // The orchestrator renders toasts for unpausable games and the toast variant.
  const isToast = !pausable || data.abcKey === IN_GAME_MERCH_ABC_KEY.TOAST;
  const overlayModifier = isToast
    ? 'in-game-merch-overlay--toast'
    : 'in-game-merch-overlay--blocking';
  const overlayClassName = `in-game-merch-overlay ${overlayModifier}${
    hasVisibleContent ? ' in-game-merch-overlay--has-content' : ''
  }`;

  return (
    <div className={overlayClassName} onClick={handleOverlayClick}>
      <div ref={contentRef}>
        <InGameMerchOrchestrator
          mdsEvent={{
            subtype: data.subtype,
            payload: data.payload,
            createdTs: data.createdTs,
          }}
          gameId={gameId ?? ''}
          pausable={pausable}
          abcKey={data.abcKey}
          brand={brand}
          config={IN_GAME_MERCH_CONFIG}
          callbacks={{ onDismiss: handleDismiss }}
        />
      </div>
    </div>
  );
};
